import { useState } from "react";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Undo2, CheckCircle2, XCircle, FileText, Loader2 } from "lucide-react";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const badge = (s) => ({ DRAFT: "border-amber-200 bg-amber-50 text-amber-700", CONFIRMED: "border-emerald-200 bg-emerald-50 text-emerald-700", CANCELLED: "border-stone-200 bg-stone-100 text-stone-500" }[s] || "border-stone-200 bg-stone-50 text-stone-600");

export default function SalesReturns() {
  const { data: returnsRaw, refetch } = useResource("/sales-returns");
  const returns = Array.isArray(returnsRaw) ? returnsRaw : [];
  const { data: billsRaw } = useResource("/sales-bills");
  const bills = (Array.isArray(billsRaw) ? billsRaw : []).filter((b) => b.status !== "CANCELLED");

  const [open, setOpen] = useState(false);
  const [billId, setBillId] = useState("");
  const [ret, setRet] = useState(null); // returnable data
  const [qty, setQty] = useState({});
  const [reason, setReason] = useState("");
  const [returnMode, setReturnMode] = useState("STOCK_RETURN");
  const [busy, setBusy] = useState(false);

  const loadReturnable = async (bid) => {
    setBillId(bid); setRet(null); setQty({});
    if (!bid) return;
    try { const { data } = await api.get(`/sales-bills/${bid}/returnable`); setRet(data); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not load invoice lines")); }
  };

  const create = async () => {
    const rlines = (ret?.lines || []).map((l) => ({ lineId: l.lineId, qty: Number(qty[l.lineId] || 0), batchNo: "" })).filter((x) => x.qty > 0);
    if (rlines.length === 0) return toast.error("Enter a return quantity");
    setBusy(true);
    try {
      await api.post("/sales-returns", { invoiceId: billId, reason, returnMode: ret?.source === "TALLY_IMPORT" ? returnMode : undefined, lines: rlines });
      toast.success("Sales return created (DRAFT)");
      setOpen(false); setBillId(""); setRet(null); setQty({}); setReason(""); refetch();
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); } finally { setBusy(false); }
  };

  const confirm = async (r) => { try { await api.post(`/sales-returns/${r.id}/confirm`, {}); toast.success("Return confirmed"); refetch(); } catch (e) { toast.error(apiErrorMessage(e, "Confirm failed")); } };
  const cancel = async (r) => { const reason = window.prompt("Cancellation reason?") || ""; try { await api.post(`/sales-returns/${r.id}/cancel`, { reason }); toast.success("Return cancelled"); refetch(); } catch (e) { toast.error(apiErrorMessage(e, "Cancel failed")); } };
  const raiseCredit = async (r) => { try { const { data } = await api.post("/credit-notes", { invoiceId: r.invoiceId, returnId: r.id }); toast.success(`Credit Note ${data.creditNoteNo} raised`); refetch(); } catch (e) { toast.error(apiErrorMessage(e, "Credit note failed")); } };

  return (
    <div className="space-y-6">
      <PageHeader title="Sales Returns" subtitle="Batch-aware, ledger-backed returns against sales invoices · confirmed returns can raise a Credit Note">
        <Button data-testid="sr-new-btn" className="gap-2 bg-amber-700 hover:bg-amber-800" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> New Return</Button>
      </PageHeader>

      <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="sr-list">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-left text-xs uppercase text-stone-500"><tr>{["Return #", "Date", "Invoice", "Buyer", "Mode", "Value", "Status", ""].map((h) => <th key={h} className="px-3 py-2.5">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-stone-100">
            {returns.length === 0 ? <tr><td colSpan={8} className="px-3 py-6 text-center text-stone-400">No sales returns yet.</td></tr> :
              returns.map((r) => (
                <tr key={r.id} data-testid={`sr-row-${r.returnNo}`} className="hover:bg-stone-50/60">
                  <td className="px-3 py-2 font-mono text-xs">{r.returnNo}</td><td className="px-3 py-2">{r.returnDate}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.invoiceNo}</td><td className="px-3 py-2">{r.buyerName}</td>
                  <td className="px-3 py-2 text-[11px]">{r.returnMode}{r.source === "TALLY_IMPORT" && <span className="ml-1 rounded bg-sky-50 px-1 text-sky-700">Tally</span>}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(r.returnValue)}</td>
                  <td className="px-3 py-2"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${badge(r.status)}`}>{r.status}</span></td>
                  <td className="flex gap-1 px-3 py-1.5">
                    {r.status === "DRAFT" && <Button size="sm" className="h-7 gap-1 bg-emerald-600 px-2 text-[11px] hover:bg-emerald-700" data-testid={`sr-confirm-${r.returnNo}`} onClick={() => confirm(r)}><CheckCircle2 className="h-3 w-3" /> Confirm</Button>}
                    {r.status === "DRAFT" && <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-500" data-testid={`sr-cancel-${r.returnNo}`} onClick={() => cancel(r)}><XCircle className="h-4 w-4" /></Button>}
                    {r.status === "CONFIRMED" && <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-[11px]" data-testid={`sr-credit-${r.returnNo}`} onClick={() => raiseCredit(r)}><FileText className="h-3 w-3" /> Raise Credit Note</Button>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white sm:max-w-2xl" data-testid="sr-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Undo2 className="h-4 w-4 text-amber-700" /> New Sales Return</DialogTitle>
            <DialogDescription className="text-xs">Select the original invoice, then enter the quantity to return per line (cannot exceed eligible).</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Original Invoice</Label>
              <select data-testid="sr-invoice" value={billId} onChange={(e) => loadReturnable(e.target.value)} className="h-9 w-full rounded-md border border-stone-200 bg-white px-3 text-sm">
                <option value="">Select invoice…</option>
                {bills.map((b) => <option key={b.id} value={b.id}>{b.billNo || b.externalInvoiceNumber} · {b.buyerName} · {money(b.grandTotal)}</option>)}
              </select></div>
            {ret && (
              <>
                <div className="text-xs text-stone-500">Buyer <b>{ret.buyerName}</b> · Invoice {ret.invoiceNo} · {ret.invoiceDate} · <span className={ret.source === "TALLY_IMPORT" ? "text-sky-700" : "text-emerald-700"}>{ret.source}</span></div>
                {ret.source === "TALLY_IMPORT" && (
                  <div className="rounded-md border border-sky-200 bg-sky-50 p-2">
                    <Label className="text-xs">Return Mode (Tally backdated invoice)</Label>
                    <Select value={returnMode} onValueChange={setReturnMode}><SelectTrigger data-testid="sr-mode" className="mt-1 h-8 bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="FINANCIAL_ONLY">Financial only (no current FG change)</SelectItem>
                        <SelectItem value="STOCK_RETURN">Stock return (add current FG back)</SelectItem>
                      </SelectContent></Select>
                  </div>
                )}
                <div className="max-h-64 overflow-auto rounded-md border border-stone-200">
                  <table className="w-full text-xs">
                    <thead className="bg-stone-50 text-left text-[10px] uppercase text-stone-500"><tr>{["Product", "Sold", "Returned", "Eligible", "Rate", "Return Qty"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-stone-100">
                      {ret.lines.map((l) => (
                        <tr key={l.lineId}>
                          <td className="px-2 py-1.5">{l.name}</td><td className="px-2 py-1.5">{l.soldQty}</td>
                          <td className="px-2 py-1.5">{l.alreadyReturned}</td><td className="px-2 py-1.5 font-semibold">{l.eligibleQty}</td>
                          <td className="px-2 py-1.5">{money(l.rate)}</td>
                          <td className="px-2 py-1.5"><Input type="number" min="0" max={l.eligibleQty} data-testid={`sr-qty-${l.lineId}`} value={qty[l.lineId] || ""} onChange={(e) => setQty({ ...qty, [l.lineId]: e.target.value })} className="h-7 w-20 text-right" disabled={l.eligibleQty <= 0} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div><Label className="text-xs">Reason</Label><Input data-testid="sr-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="h-9" placeholder="Damaged / short / quality…" /></div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="sr-create" className="gap-2 bg-amber-700 hover:bg-amber-800" disabled={busy || !ret} onClick={create}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} Create Return</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
